import React, { ForwardedRef, forwardRef, useEffect, useState } from 'react';
import { Fund } from '../api/useFunds';
import { CashbackModel } from '../utils/cashbackConstants';
import DetailCalculator, { CalculatorState } from './DetailCalculator';
import { mapToCache, bicFromCache } from './cashbackCalculatorUtil';
import { useInitialState } from './hooks/useInitialState';

export interface DetailCalculatorWithLocalStorageProps {
  model?: CashbackModel;
  bookNowUrl: string;
  onStateChange?: (state: Record<string, number>, bic: Bic) => void;
  defaultState?: Record<string, number>;
  defaultBic?: string;
  onFundSelect?: (fund: Fund) => void;
}

export const DetailCalculatorWithLocalStorage = forwardRef(
  (
    {
      bookNowUrl,
      model = 'flat-fee',
      onStateChange,
      defaultState,
      defaultBic,
      onFundSelect,
    }: DetailCalculatorWithLocalStorageProps,
    ref: ForwardedRef<CalculatorState>
  ) => {
    const [initialBic, setInitialBic] = useState(defaultBic);
    const { data: initialState, isLoading } = useInitialState(defaultState);

    useEffect(() => {
      if (!defaultBic) {
        setInitialBic(bicFromCache());
      }
    }, [defaultBic]);

    const handleStateChange = (state: Map<Fund, number>, bic: Bic) => {
      const isinToValue: Record<string, number> = {};
      state.forEach((v, k) => {
        isinToValue[k.isin || ''] = v;
      });
      onStateChange && onStateChange(isinToValue, bic);
      mapToCache(state, bic);
    };

    if (isLoading && defaultState) {
      return null;
    }

    return (
      <DetailCalculator
        model={model}
        ref={ref}
        initialState={initialState}
        initialBic={initialBic as Bic}
        onStateChange={handleStateChange}
        bookNowUrl={bookNowUrl}
        onFundSelect={onFundSelect}
      />
    );
  }
);

export default DetailCalculatorWithLocalStorage;
